import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Alert,
  Button,
  Card,
  CardBody,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from 'reactstrap'
import moment from 'moment'
import qs from 'qs'
import ReactApexChart from 'react-apexcharts'
import oikosApi from '../../../../services/oikos'
import { notifyError, notifySuccess } from '../../../../utils/notify'

const DEFAULT_FORM_DEF = JSON.stringify(
  [
    {
      id: 's1',
      title: '1 - Identificação',
      icon: 'business',
      fields: [
        {
          name: 'observacoes',
          label: 'Observações',
          type: 'multiline',
          required: false,
        },
      ],
    },
  ],
  null,
  2,
)

const formatDateTime = (v) => (v ? moment(v).format('DD/MM/YYYY HH:mm') : '—')

const RESP_NOME_FIELD = '(SELECT pf_nomeExibicao FROM pf WHERE pf.pf_id = projFormResp.pf_id LIMIT 1)'

const RESP_SEM_FOTO = ['projFormResp_dados']

const RESP_FOTO_TEXTO = "REGEXP_REPLACE(projFormResp_dados, 'data:image/[A-Za-z0-9.+-]+;base64,[A-Za-z0-9+/=]+', '[imagem]')"

const tryPrettyJson = (raw) => {
  if (raw == null || raw === '') return ''
  if (typeof raw === 'object') {
    try {
      return JSON.stringify(raw, null, 2)
    } catch {
      return String(raw)
    }
  }
  try {
    return JSON.stringify(JSON.parse(String(raw)), null, 2)
  } catch {
    return String(raw)
  }
}

const parseJsonLoose = (raw) => {
  if (raw == null || raw === '') return null
  if (typeof raw === 'object') return raw
  try {
    return JSON.parse(String(raw))
  } catch {
    return null
  }
}

const humanizeKey = (key) => {
  const text = String(key)
    .replace(/___/g, ' - ')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!text) return String(key)
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const isEmptyValue = (value) => {
  if (value == null) return true
  if (typeof value === 'string' && value.trim() === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

const dataUriFromBase64 = (raw, filename) => {
  const text = String(raw || '').trim()
  if (!text) return null
  if (text.startsWith('data:image/')) return text
  const compact = text.replace(/\s/g, '')
  if (compact.startsWith('/9j/')) return `data:image/jpeg;base64,${compact}`
  if (compact.startsWith('iVBORw0KGgo')) return `data:image/png;base64,${compact}`
  if (compact.startsWith('R0lGOD')) return `data:image/gif;base64,${compact}`
  if (compact.startsWith('UklGR')) return `data:image/webp;base64,${compact}`
  const name = String(filename || '').toLowerCase()
  const mime = name.endsWith('.png') ? 'image/png'
    : name.endsWith('.gif') ? 'image/gif'
    : name.endsWith('.webp') ? 'image/webp'
    : name.endsWith('.bmp') ? 'image/bmp'
    : (name.endsWith('.jpg') || name.endsWith('.jpeg')) ? 'image/jpeg'
    : null
  if (!mime || !/^[A-Za-z0-9+/=]+$/.test(compact)) return null
  return `data:${mime};base64,${compact}`
}

const imageSrcOf = (value) => {
  if (typeof value === 'string') {
    const text = value.trim()
    if (text.startsWith('data:image/')) return text
    if (/^https?:\/\//i.test(text) && /\.(png|jpe?g|gif|webp|bmp)(\?|#|$)/i.test(text)) return text
    return null
  }
  if (value && typeof value === 'object') {
    const candidate = value.url || value.uri || value.src || value.dataUrl
    if (typeof candidate === 'string') {
      const src = imageSrcOf(candidate)
      if (src) return src
    }
    if (typeof value.data === 'string' && value.data.startsWith('data:image/')) return value.data
    const fromContent = dataUriFromBase64(value.content, value.filename || value.fileName || value.name)
    if (fromContent) return fromContent
  }
  return null
}

const fileCaption = (value) => {
  if (typeof value === 'string') {
    const text = value.trim()
    if (!text || text.startsWith('data:')) return 'Arquivo anexado'
    if (/^https?:\/\//i.test(text)) {
      try {
        const url = new URL(text)
        const name = decodeURIComponent(url.pathname.split('/').filter(Boolean).pop() || '')
        return name || 'Arquivo anexado'
      } catch {
        return 'Arquivo anexado'
      }
    }
    if (text.includes('/')) {
      try {
        return decodeURIComponent(text.split('/').pop() || text)
      } catch {
        return text.split('/').pop() || text
      }
    }
    return text
  }
  if (value && typeof value === 'object') {
    const name = value.label || value.name || value.filename || value.fileName
    if (name != null && String(name).trim() !== '') return String(name)
  }
  return 'Arquivo anexado'
}

const optionLabelFor = (field, value) => {
  const options = Array.isArray(field?.options) ? field.options : []
  if (!options.length) return null
  const scalar = value != null && typeof value === 'object'
    ? (value.value ?? value.id ?? value.key ?? value.name ?? value.label)
    : value
  if (scalar == null || scalar === '') return null
  const sameText = (left, right) => String(left).trim().toLocaleLowerCase('pt-BR') === String(right).trim().toLocaleLowerCase('pt-BR')
  const found = options.find((opt) => {
    if (opt != null && typeof opt === 'object') {
      return [opt.value, opt.id, opt.key, opt.name, opt.label].some(
        (item) => item != null && sameText(item, scalar),
      )
    }
    return sameText(opt, scalar)
  })
  if (found != null) {
    if (typeof found === 'object') {
      if (found.label != null && String(found.label).trim() !== '') return String(found.label)
      if (found.name != null && String(found.name).trim() !== '') return String(found.name)
    }
    return String(found)
  }
  if (field?.type !== 'dropdown') return null
  const indexText = typeof scalar === 'number' ? String(scalar) : (typeof scalar === 'string' ? scalar.trim() : '')
  if (!/^\d+$/.test(indexText)) return null
  const byIndex = options[Number(indexText)]
  if (byIndex == null) return null
  if (typeof byIndex === 'object') {
    if (byIndex.label != null && String(byIndex.label).trim() !== '') return String(byIndex.label)
    if (byIndex.name != null) return String(byIndex.name)
  }
  return String(byIndex)
}

const formatDateLabel = (value) => {
  const text = String(value).trim()
  const parsed = moment(text, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ', 'DD/MM/YYYY', 'DD/MM/YYYY HH:mm'], true)
  if (parsed.isValid()) return parsed.format('DD/MM/YYYY')
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const loose = moment(text)
    if (loose.isValid()) return loose.format('DD/MM/YYYY')
  }
  return text
}

const formatTimeLabel = (value) => {
  const text = String(value).trim()
  const parsed = moment(text, ['HH:mm', 'HH:mm:ss', 'HH:mm:ss.SSS'], true)
  if (parsed.isValid()) return parsed.format('HH:mm')
  if (/T\d{2}:\d{2}/.test(text)) {
    const loose = moment(text)
    if (loose.isValid()) return loose.format('HH:mm')
  }
  return text
}

const formatNumberLabel = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value.toLocaleString('pt-BR')
  const text = String(value).trim()
  if (/^-?\d+$/.test(text) || /^-?\d+\.\d+$/.test(text)) return Number(text).toLocaleString('pt-BR')
  if (/^-?\d+,\d+$/.test(text)) return Number(text.replace(',', '.')).toLocaleString('pt-BR')
  return text
}

const booleanLabel = (value) => {
  if (value === true || value === 1 || value === '1' || value === 'true' || value === 'sim' || value === 'Sim') return 'Sim'
  if (value === false || value === 0 || value === '0' || value === 'false' || value === 'nao' || value === 'não' || value === 'Não') return 'Não'
  return null
}

const plainScalar = (field, value) => {
  if (typeof value === 'boolean' || field?.type === 'boolean') {
    const label = booleanLabel(value)
    if (label) return label
  }
  if (value == null) return ''
  const text = String(value).trim()
  if (!text) return ''
  if (field?.type === 'date') return formatDateLabel(text)
  if (field?.type === 'time') return formatTimeLabel(text)
  if (field?.type === 'number') return formatNumberLabel(value)
  if (!field && /^\d{4}-\d{2}-\d{2}$/.test(text)) return formatDateLabel(text)
  return text
}

const describeValue = (field, value) => {
  if (isEmptyValue(value)) return { kind: 'empty' }

  if (Array.isArray(value)) {
    const images = []
    const texts = []
    value.forEach((item) => {
      const part = describeValue(field, item)
      if (part.kind === 'images') {
        images.push(...part.images)
        if (part.text) texts.push(part.text)
      } else if ((part.kind === 'text' || part.kind === 'link') && part.text) {
        texts.push(part.text)
      }
    })
    if (images.length) return { kind: 'images', images, text: texts.join('\n') }
    if (!texts.length) return { kind: 'empty' }
    return { kind: 'text', text: texts.join(field?.type === 'multiline' ? '\n' : ', ') }
  }

  const src = imageSrcOf(value)
  if (src) {
    const caption = field?.type === 'signature' ? '' : fileCaption(value)
    const showCaption = caption && caption !== 'Arquivo anexado' && caption !== src
    return {
      kind: 'images',
      images: [{ src, alt: field?.label || (field?.type === 'signature' ? 'Assinatura' : 'Imagem') }],
      text: showCaption ? caption : '',
    }
  }

  const asBool = (field?.type === 'boolean' || typeof value === 'boolean') ? booleanLabel(value) : null
  if (asBool) return { kind: 'text', text: asBool }

  const fromOption = optionLabelFor(field, value)
  if (fromOption != null) return { kind: 'text', text: fromOption }

  if (field?.type === 'signature') return { kind: 'text', text: 'Assinatura registrada' }

  if (field?.type === 'file') {
    if (typeof value === 'string' && /^https?:\/\//i.test(value.trim())) {
      return { kind: 'link', href: value.trim(), text: fileCaption(value) }
    }
    return { kind: 'text', text: fileCaption(value) }
  }

  if (value && typeof value === 'object') {
    if (value.label != null && String(value.label).trim() !== '') return { kind: 'text', text: String(value.label) }
    const name = value.name || value.filename || value.fileName
    if (name != null && String(name).trim() !== '') return { kind: 'text', text: String(name) }
    const pairs = Object.entries(value).filter(([, item]) => !isEmptyValue(item)).slice(0, 12)
    if (!pairs.length) return { kind: 'empty' }
    return {
      kind: 'text',
      text: pairs.map(([key, item]) => `${humanizeKey(key)}: ${plainScalar(null, item) || '—'}`).join('\n'),
    }
  }

  const text = plainScalar(field, value)
  return text ? { kind: 'text', text } : { kind: 'empty' }
}

const buildFichaGroups = (defRaw, dadosRaw) => {
  const parsedDef = parseJsonLoose(defRaw)
  const sections = Array.isArray(parsedDef) ? parsedDef : []
  const parsedDados = parseJsonLoose(dadosRaw)
  const dados = parsedDados && typeof parsedDados === 'object' && !Array.isArray(parsedDados) ? parsedDados : {}
  const used = new Set()
  const groups = []

  const pushFields = (title, fields) => {
    const rows = []
    const list = fields || []
    list.forEach((field) => {
      if (!field || field.name == null || field.name === '') return
      const name = String(field.name)
      used.add(name)
      rows.push({
        key: name,
        label: field.label || humanizeKey(name),
        field,
        value: Object.prototype.hasOwnProperty.call(dados, name) ? dados[name] : undefined,
      })
    })
    if (rows.length) groups.push({ id: `grupo-${groups.length + 1}`, title: title || 'Campos', rows })
  }

  sections.forEach((section, index) => {
    const title = section?.title || `Seção ${index + 1}`
    if (Array.isArray(section?.fields) && section.fields.length) pushFields(title, section.fields)
    const subsections = section?.subsections || []
    subsections.forEach((sub, subIndex) => {
      const subTitle = sub?.title ? `${title} · ${sub.title}` : `${title} · ${subIndex + 1}`
      pushFields(subTitle, sub?.fields)
    })
  })

  const extraRows = Object.keys(dados)
    .filter((key) => !used.has(key))
    .map((key) => ({
      key,
      label: humanizeKey(key),
      field: null,
      value: dados[key],
    }))
  if (extraRows.length) groups.push({ id: 'outros', title: 'Outros campos', rows: extraRows })

  if (!parsedDados && dadosRaw != null && String(dadosRaw).trim() !== '') {
    groups.push({
      id: 'conteudo',
      title: 'Conteúdo',
      rows: [{ key: '_raw', label: 'Conteúdo', field: null, value: String(dadosRaw) }],
    })
  }

  return {
    groups,
    defMissing: !Array.isArray(parsedDef),
  }
}

const fichaCellText = (field, value) => {
  const display = describeValue(field, value)
  if (!display || display.kind === 'empty') return ''
  if (display.kind === 'link') return display.href || display.text || ''
  if (display.kind === 'images') {
    const urls = (display.images || [])
      .map((img) => img.src)
      .filter((src) => src && !String(src).startsWith('data:'))
    if (urls.length) return urls.join(' ')
    if (display.text) return display.text
    return field?.type === 'signature' ? 'Assinatura registrada' : 'Imagem anexada'
  }
  return display.text || ''
}

const collectExportColumns = (defRaw, responses) => {
  const base = buildFichaGroups(defRaw, {})
  const columns = []
  const seen = new Set()
  base.groups.forEach((group) => {
    group.rows.forEach((row) => {
      if (seen.has(row.key) || row.key === '_raw') return
      seen.add(row.key)
      columns.push({
        key: row.key,
        label: row.label,
        field: row.field,
        section: group.title,
      })
    })
  })
  const list = responses || []
  list.forEach((response) => {
    const dados = parseJsonLoose(response?.projFormResp_dados)
    if (!dados || typeof dados !== 'object' || Array.isArray(dados)) return
    Object.keys(dados).forEach((key) => {
      if (seen.has(key)) return
      seen.add(key)
      columns.push({
        key,
        label: humanizeKey(key),
        field: null,
        section: 'Outros campos',
      })
    })
  })
  const counts = {}
  columns.forEach((column) => {
    counts[column.label] = (counts[column.label] || 0) + 1
  })
  return columns.map((column) => ({
    ...column,
    header: counts[column.label] > 1 ? `${column.section} / ${column.label}` : column.label,
  }))
}

const csvCell = (value) => {
  let text = value == null ? '' : String(value)
  const numeric = /^-?\d{1,3}(\.\d{3})*(,\d+)?$/.test(text) || /^-?\d+(,\d+)?$/.test(text)
  if (!numeric && /^[=+\-@]/.test(text)) text = `'${text}`
  if (/[;"\r\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`
  return text
}

const buildFormCsv = (form, responses) => {
  const columns = collectExportColumns(form?.projForm_def, responses)
  const ordered = [...(responses || [])].sort((a, b) => {
    const da = a?.projFormResp_dtCria ? moment(a.projFormResp_dtCria).valueOf() : 0
    const db = b?.projFormResp_dtCria ? moment(b.projFormResp_dtCria).valueOf() : 0
    if (da !== db) return da - db
    return Number(a?.projFormResp_id) - Number(b?.projFormResp_id)
  })
  const headers = [
    'ID',
    'Preenchido por',
    'Versão do formulário',
    'Data do registro',
    ...columns.map((column) => column.header),
  ]
  const lines = [headers.map(csvCell).join(';')]
  ordered.forEach((response) => {
    const dados = parseJsonLoose(response?.projFormResp_dados)
    const record = dados && typeof dados === 'object' && !Array.isArray(dados) ? dados : {}
    const metaDate = formatDateTime(response?.projFormResp_dtCria)
    const row = [
      response?.projFormResp_id ?? '',
      response?.pf_nome || (response?.pf_id ? `PF #${response.pf_id}` : ''),
      response?.projFormResp_versaoForm || '',
      metaDate === '—' ? '' : metaDate,
      ...columns.map((column) => (
        Object.prototype.hasOwnProperty.call(record, column.key)
          ? fichaCellText(column.field, record[column.key])
          : ''
      )),
    ]
    lines.push(row.map(csvCell).join(';'))
  })
  return lines.join('\r\n')
}

const csvFileName = (form) => {
  const raw = String(form?.projForm_nome || 'coleta').trim() || 'coleta'
  const safe = raw.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80)
  return `${safe || 'coleta'}.csv`
}

const downloadCsvFile = (filename, csv) => {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const FichaValue = ({ field, value }) => {
  const display = describeValue(field, value)
  if (display.kind === 'empty') return <span className="text-muted">—</span>
  if (display.kind === 'images') {
    return (
      <div className="d-flex flex-column" style={{ gap: 8 }}>
        {display.images.map((img) => (
          <img
            key={`${img.alt}-${img.src.length}-${img.src.slice(-16)}`}
            src={img.src}
            alt={img.alt}
            style={{
              maxWidth: '100%',
              maxHeight: 240,
              objectFit: 'contain',
              borderRadius: 4,
              border: '1px solid #e9ecef',
              background: '#fff',
            }}
          />
        ))}
        {display.text ? <span>{display.text}</span> : null}
      </div>
    )
  }
  if (display.kind === 'link') {
    return (
      <a href={display.href} target="_blank" rel="noopener noreferrer">
        {display.text}
      </a>
    )
  }
  return <span style={{ whiteSpace: 'pre-wrap' }}>{display.text}</span>
}

const FichaView = ({ groups, defMissing }) => {
  if (!groups.length) {
    return (
      <div className="text-muted" style={{ fontSize: 13 }}>
        Nenhum dado neste registro.
      </div>
    )
  }
  return (
    <div style={{ maxHeight: 520, overflow: 'auto' }}>
      {defMissing ? (
        <div className="text-muted" style={{ fontSize: 12, marginBottom: 8 }}>
          A definição do formulário não está disponível. Os rótulos foram montados a partir dos nomes dos campos.
        </div>
      ) : null}
      {groups.map((group) => (
        <div
          key={group.id}
          style={{
            border: '1px solid #e9ecef',
            borderRadius: 6,
            marginBottom: 12,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              background: '#f8f9fa',
              padding: '8px 12px',
              fontWeight: 700,
              fontSize: 13,
              color: '#343a40',
              borderBottom: '1px solid #e9ecef',
            }}
          >
            {group.title}
          </div>
          {group.rows.map((row, rowIndex) => (
            <div
              key={`${group.id}-${row.key}`}
              style={{
                padding: '10px 12px',
                borderBottom: rowIndex < group.rows.length - 1 ? '1px solid #f1f3f5' : 'none',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6c757d', marginBottom: 2 }}>
                {row.label}
              </div>
              <div style={{ fontSize: 14, color: '#212529', wordBreak: 'break-word' }}>
                <FichaValue field={row.field} value={row.value} />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

const PIE_COLORS = ['#5664d2', '#34c38f', '#f1b44c', '#f46a6a', '#50a5f1', '#74788d', '#e83e8c', '#20c997', '#6f42c1', '#fd7e14']

const collectorName = (row) => {
  const name = row?.pf_nome != null ? String(row.pf_nome).trim() : ''
  if (name) return name
  if (row?.pf_id) return `PF #${row.pf_id}`
  return 'Sem identificação'
}

const buildColetaStats = (rows, now) => {
  const clock = now ? moment(now) : moment()
  const todayStart = clock.clone().startOf('day')
  const weekStart = todayStart.clone().subtract(6, 'days')
  const monthStart = todayStart.clone().subtract(29, 'days')
  const byUser = new Map()
  const byDay = new Map()
  for (let i = 0; i < 30; i += 1) {
    const day = monthStart.clone().add(i, 'days')
    byDay.set(day.format('YYYY-MM-DD'), 0)
  }
  let today = 0
  let week = 0
  let month = 0
  const list = rows || []
  list.forEach((row) => {
    const name = collectorName(row)
    byUser.set(name, (byUser.get(name) || 0) + 1)
    const created = row?.projFormResp_dtCria ? moment(row.projFormResp_dtCria) : null
    if (!created || !created.isValid()) return
    const ts = created.valueOf()
    const key = created.format('YYYY-MM-DD')
    if (ts >= todayStart.valueOf()) today += 1
    if (ts >= weekStart.valueOf()) week += 1
    if (ts >= monthStart.valueOf()) {
      month += 1
      if (byDay.has(key)) byDay.set(key, (byDay.get(key) || 0) + 1)
    }
  })
  const users = [...byUser.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'pt-BR'))
  const days = [...byDay.entries()].map(([iso, count]) => ({
    iso,
    label: moment(iso, 'YYYY-MM-DD').format('DD/MM'),
    count,
  }))
  return { today, week, month, users, days }
}

const ColetaResumo = ({ stats, loading }) => {
  const waiting = loading && stats.users.length === 0 && stats.today === 0 && stats.month === 0
  const cards = [
    { id: 'hoje', label: 'Hoje', value: stats.today },
    { id: 'semana', label: 'Últimos 7 dias', value: stats.week },
    { id: 'mes', label: 'Últimos 30 dias', value: stats.month },
  ]
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: 12 }}>
        {cards.map((card) => (
          <div
            key={card.id}
            style={{
              border: '1px solid #e9ecef',
              borderRadius: 8,
              background: '#fff',
              padding: '14px 16px',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6c757d' }}>{card.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#343a40', lineHeight: 1.15, marginTop: 4 }}>
              {waiting ? '…' : card.value}
            </div>
            <div style={{ fontSize: 12, color: '#6c757d' }}>coletas</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) minmax(0, 1fr)', gap: 12 }}>
        <div style={{ border: '1px solid #e9ecef', borderRadius: 8, background: '#fff', padding: '12px 12px 4px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#343a40', marginBottom: 4 }}>Por usuário</div>
          {waiting ? (
            <div className="text-muted" style={{ fontSize: 13, padding: '24px 0' }}>Carregando…</div>
          ) : stats.users.length === 0 ? (
            <div className="text-muted" style={{ fontSize: 13, padding: '24px 0' }}>Nenhuma coleta registrada.</div>
          ) : (
            <ReactApexChart
              type="pie"
              height={280}
              series={stats.users.map((user) => user.count)}
              options={{
                labels: stats.users.map((user) => user.name),
                colors: PIE_COLORS,
                chart: { fontFamily: 'inherit', toolbar: { show: false } },
                legend: { position: 'bottom', fontSize: '12px' },
                dataLabels: {
                  enabled: true,
                  formatter: (value) => `${Math.round(value)}%`,
                },
                stroke: { width: 1, colors: ['#fff'] },
                tooltip: {
                  y: { formatter: (value) => `${value} ${value === 1 ? 'coleta' : 'coletas'}` },
                },
              }}
            />
          )}
        </div>
        <div style={{ border: '1px solid #e9ecef', borderRadius: 8, background: '#fff', padding: '12px 8px 4px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#343a40', paddingLeft: 8 }}>Coletas por dia</div>
          <div className="text-muted" style={{ fontSize: 12, paddingLeft: 8, marginBottom: 4 }}>Últimos 30 dias</div>
          {waiting ? (
            <div className="text-muted" style={{ fontSize: 13, padding: '24px 8px' }}>Carregando…</div>
          ) : (
            <ReactApexChart
              type="bar"
              height={280}
              series={[{ name: 'Coletas', data: stats.days.map((day) => day.count) }]}
              options={{
                chart: { type: 'bar', fontFamily: 'inherit', toolbar: { show: false } },
                plotOptions: { bar: { borderRadius: 2, columnWidth: '62%' } },
                colors: ['#5664d2'],
                dataLabels: { enabled: false },
                grid: { borderColor: '#f1f3f7' },
                xaxis: {
                  categories: stats.days.map((day) => day.label),
                  labels: {
                    rotate: -45,
                    rotateAlways: true,
                    hideOverlappingLabels: false,
                    style: { fontSize: '10px', colors: '#6c757d' },
                  },
                },
                yaxis: {
                  min: 0,
                  forceNiceScale: true,
                  labels: {
                    formatter: (value) => String(Math.round(value)),
                    style: { fontSize: '11px', colors: '#6c757d' },
                  },
                },
                tooltip: {
                  y: { formatter: (value) => `${value} ${value === 1 ? 'coleta' : 'coletas'}` },
                },
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

const sidebarItemStyle = (active) => ({
  cursor: 'pointer',
  padding: '10px 12px',
  borderRadius: 6,
  border: active ? '1px solid rgba(86,100,210,0.45)' : '1px solid transparent',
  background: active ? 'rgba(86,100,210,0.08)' : 'transparent',
  marginBottom: 6,
  transition: 'background 0.15s ease, border 0.15s ease',
})

const ProjDadosTab = ({ project }) => {
  const { user } = useSelector((state) => state.auth)
  const projId = project?.proj_id
  const pfId = user?.pf_id

  const [loadingForms, setLoadingForms] = useState(true)
  const [forms, setForms] = useState([])
  const [selectedFormId, setSelectedFormId] = useState(null)

  const [loadingResps, setLoadingResps] = useState(false)
  const [resps, setResps] = useState([])
  const [projectResps, setProjectResps] = useState([])
  const [loadingStats, setLoadingStats] = useState(false)

  const [editorOpen, setEditorOpen] = useState(false)
  const [editorSaving, setEditorSaving] = useState(false)
  const [editingFormId, setEditingFormId] = useState(null) // null = criar
  const [editNome, setEditNome] = useState('')
  const [editVersao, setEditVersao] = useState('1')
  const [editDef, setEditDef] = useState(DEFAULT_FORM_DEF)
  const [saveChoiceOpen, setSaveChoiceOpen] = useState(false)

  const [respModalOpen, setRespModalOpen] = useState(false)
  const [selectedResp, setSelectedResp] = useState(null)
  const [respView, setRespView] = useState('ficha')
  const [respLoading, setRespLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const respLoadSeq = useRef(0)

  const isEditing = editingFormId != null
  const editingOriginal = useMemo(
    () => forms.find((f) => Number(f.projForm_id) === Number(editingFormId)) || null,
    [forms, editingFormId],
  )

  const selectedForm = useMemo(
    () => forms.find((f) => Number(f.projForm_id) === Number(selectedFormId)) || null,
    [forms, selectedFormId],
  )

  const ficha = useMemo(
    () => buildFichaGroups(selectedForm?.projForm_def, selectedResp?.projFormResp_dados),
    [selectedForm, selectedResp],
  )

  const coletaStats = useMemo(() => buildColetaStats(projectResps), [projectResps])

  const loadProjectResps = useCallback(async (formRows) => {
    const ids = (formRows || []).map((form) => Number(form.projForm_id)).filter((id) => id > 0)
    if (!ids.length) {
      setProjectResps([])
      setLoadingStats(false)
      return
    }
    setLoadingStats(true)
    try {
      const res = await oikosApi.get(
        `/?${qs.stringify({
          action: 'get',
          table: 'projFormResp',
          data: {
            projForm_id: `!IN!(${ids.join(',')})`,
            projFormResp_ativo: 1,
          },
          fields: {
            [RESP_NOME_FIELD]: 'pf_nome',
          },
          exclude: RESP_SEM_FOTO,
        })}`,
      )
      const rows = Array.isArray(res?.data) ? res.data : []
      setProjectResps(rows)
    } catch (e) {
      console.error('ProjDadosTab loadProjectResps', e)
      notifyError('Não foi possível carregar o resumo das coletas.')
      setProjectResps([])
    } finally {
      setLoadingStats(false)
    }
  }, [])

  const loadForms = useCallback(async () => {
    if (!projId) {
      setForms([])
      setSelectedFormId(null)
      setProjectResps([])
      setLoadingForms(false)
      return
    }
    setLoadingForms(true)
    setProjectResps([])
    try {
      const res = await oikosApi.get(
        `/?${qs.stringify({
          action: 'get',
          table: 'projForm',
          data: { proj_id: projId, projForm_ativo: 1 },
        })}`,
      )
      const rows = Array.isArray(res?.data) ? res.data : []
      rows.sort((a, b) => Number(b.projForm_id) - Number(a.projForm_id))
      setForms(rows)
      setSelectedFormId((prev) => {
        if (prev && rows.some((r) => Number(r.projForm_id) === Number(prev))) return prev
        return rows[0]?.projForm_id ?? null
      })
      loadProjectResps(rows)
    } catch (e) {
      console.error('ProjDadosTab loadForms', e)
      notifyError('Não foi possível carregar os formulários do projeto.')
      setForms([])
      setSelectedFormId(null)
      setProjectResps([])
    } finally {
      setLoadingForms(false)
    }
  }, [projId, loadProjectResps])

  const loadResps = useCallback(async (formId) => {
    if (!formId) {
      setResps([])
      return
    }
    setLoadingResps(true)
    try {
      const res = await oikosApi.get(
        `/?${qs.stringify({
          action: 'get',
          table: 'projFormResp',
          data: { projForm_id: formId, projFormResp_ativo: 1 },
          fields: {
            [RESP_NOME_FIELD]: 'pf_nome',
          },
          exclude: RESP_SEM_FOTO,
        })}`,
      )
      const rows = Array.isArray(res?.data) ? res.data : []
      rows.sort((a, b) => {
        const da = a.projFormResp_dtCria ? moment(a.projFormResp_dtCria).valueOf() : 0
        const db = b.projFormResp_dtCria ? moment(b.projFormResp_dtCria).valueOf() : 0
        return db - da
      })
      setResps(rows)
    } catch (e) {
      console.error('ProjDadosTab loadResps', e)
      notifyError('Não foi possível carregar as respostas.')
      setResps([])
    } finally {
      setLoadingResps(false)
    }
  }, [])

  useEffect(() => {
    loadForms()
  }, [loadForms])

  useEffect(() => {
    loadResps(selectedFormId)
  }, [selectedFormId, loadResps])

  const openCreate = () => {
    setEditingFormId(null)
    setEditNome('')
    setEditVersao('1')
    setEditDef(DEFAULT_FORM_DEF)
    setEditorOpen(true)
  }

  const openEdit = (form) => {
    if (!form?.projForm_id) return
    setEditingFormId(form.projForm_id)
    setEditNome(form.projForm_nome || '')
    setEditVersao(form.projForm_versao || '1')
    setEditDef(tryPrettyJson(form.projForm_def) || DEFAULT_FORM_DEF)
    setEditorOpen(true)
  }

  const closeEditor = () => {
    if (editorSaving) return
    setSaveChoiceOpen(false)
    setEditorOpen(false)
    setEditingFormId(null)
  }

  const parseEditorPayload = () => {
    const nome = String(editNome || '').trim()
    if (!nome) {
      notifyError('Informe o nome do formulário.')
      return null
    }
    if (!projId) {
      notifyError('Projeto inválido.')
      return null
    }
    let defParsed
    try {
      defParsed = JSON.parse(editDef)
    } catch {
      notifyError('O JSON da definição do formulário é inválido.')
      return null
    }
    if (!Array.isArray(defParsed)) {
      notifyError('A definição deve ser um array JSON de seções.')
      return null
    }
    const versao = String(editVersao || '1').trim() || '1'
    return { nome, defParsed, versao, defStr: JSON.stringify(defParsed) }
  }

  /** Criação direta; na edição abre confirmação (atualizar vs nova versão). */
  const requestSaveForm = () => {
    const payload = parseEditorPayload()
    if (!payload) return
    if (isEditing) {
      setSaveChoiceOpen(true)
      return
    }
    handleSaveForm('create', payload)
  }

  /**
   * @param {'create'|'update'|'new_version'} mode
   */
  const handleSaveForm = async (mode, preParsed) => {
    const payload = preParsed || parseEditorPayload()
    if (!payload) return
    const { nome, versao, defStr } = payload

    if (mode === 'new_version' && editingOriginal) {
      const currentVer = String(editingOriginal.projForm_versao || '1')
      if (versao === currentVer) {
        notifyError(
          `Para criar uma nova versão, altere o campo Versão (atual: ${currentVer}).`,
        )
        setSaveChoiceOpen(false)
        return
      }
    }

    setEditorSaving(true)
    try {
      const now = moment().format('YYYY-MM-DD HH:mm:ss')

      if (mode === 'update') {
        await oikosApi.post('/', {
          action: 'update',
          table: 'projForm',
          data: {
            projForm_id: editingFormId,
            projForm_nome: nome,
            projForm_versao: versao,
            projForm_def: defStr,
            projForm_usuarioMod: pfId || undefined,
            projForm_dtMod: now,
          },
        })
        notifySuccess('Formulário atualizado (mesma versão / registro).')
        setSaveChoiceOpen(false)
        setEditorOpen(false)
        await loadForms()
        setSelectedFormId(editingFormId)
      } else {
        // create | new_version → insert
        const res = await oikosApi.post('/', {
          action: 'insert',
          table: 'projForm',
          data: {
            proj_id: projId,
            projForm_nome: nome,
            projForm_versao: versao,
            projForm_def: defStr,
            projForm_ativo: 1,
            projForm_usuarioCria: pfId || undefined,
            projForm_usuarioMod: pfId || undefined,
            projForm_dtCria: now,
            projForm_dtMod: now,
          },
        })
        const created = Array.isArray(res?.data) ? res.data[0] : null
        notifySuccess(mode === 'new_version' ? 'Nova versão do formulário criada.' : 'Formulário criado.')
        setSaveChoiceOpen(false)
        setEditorOpen(false)
        await loadForms()
        if (created?.projForm_id) setSelectedFormId(created.projForm_id)
      }
    } catch (e) {
      console.error('ProjDadosTab save form', e)
      notifyError(
        e?.message ||
          (mode === 'update'
            ? 'Falha ao atualizar formulário.'
            : mode === 'new_version'
              ? 'Falha ao criar nova versão.'
              : 'Falha ao criar formulário.'),
      )
    } finally {
      setEditorSaving(false)
    }
  }

  const openResp = async (row) => {
    const id = Number(row?.projFormResp_id)
    const seq = respLoadSeq.current + 1
    respLoadSeq.current = seq
    setRespView('ficha')
    setSelectedResp(row)
    setRespModalOpen(true)
    if (!id || (row?.projFormResp_dados != null && row.projFormResp_dados !== '')) {
      setRespLoading(false)
      return
    }
    setRespLoading(true)
    try {
      const res = await oikosApi.get(
        `/?${qs.stringify({
          action: 'get',
          table: 'projFormResp',
          data: { projFormResp_id: id, projFormResp_ativo: 1 },
          fields: { [RESP_NOME_FIELD]: 'pf_nome' },
        })}`,
      )
      const full = Array.isArray(res?.data) ? res.data[0] : null
      if (respLoadSeq.current !== seq) return
      if (!full) {
        notifyError('Não foi possível abrir esta coleta.')
        return
      }
      setSelectedResp((current) => (
        Number(current?.projFormResp_id) === id
          ? { ...current, ...full, pf_nome: current?.pf_nome || full.pf_nome }
          : current
      ))
    } catch (e) {
      console.error('ProjDadosTab openResp', e)
      if (respLoadSeq.current === seq) notifyError('Não foi possível abrir esta coleta.')
    } finally {
      if (respLoadSeq.current === seq) setRespLoading(false)
    }
  }

  const exportResponsesCsv = async () => {
    if (!selectedForm || resps.length === 0 || exporting) return
    setExporting(true)
    try {
      const res = await oikosApi.get(
        `/?${qs.stringify({
          action: 'get',
          table: 'projFormResp',
          data: { projForm_id: selectedForm.projForm_id, projFormResp_ativo: 1 },
          exclude: RESP_SEM_FOTO,
          fields: {
            [RESP_FOTO_TEXTO]: 'projFormResp_dados',
            [RESP_NOME_FIELD]: 'pf_nome',
          },
        })}`,
      )
      const rows = Array.isArray(res?.data) ? res.data : []
      downloadCsvFile(csvFileName(selectedForm), buildFormCsv(selectedForm, rows))
    } catch (e) {
      console.error('ProjDadosTab export csv', e)
      notifyError('Não foi possível exportar a planilha.')
    } finally {
      setExporting(false)
    }
  }

  if (!projId) {
    return <Alert color="warning" className="mb-0">Projeto não carregado.</Alert>
  }

  if (loadingForms) {
    return (
      <div className="d-flex align-items-center" style={{ gap: 10, padding: '24px 0' }}>
        <Spinner size="sm" />
        <span className="text-muted" style={{ fontSize: 13 }}>Carregando formulários…</span>
      </div>
    )
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12, marginBottom: 12 }}>
        <div>
          <h5 className="mb-1" style={{ fontWeight: 700 }}>Dados de coleta</h5>
          <p className="text-muted mb-0" style={{ fontSize: 13 }}>
            Formulários do projeto e respostas enviadas pelo app.
          </p>
        </div>
        <Button color="primary" size="sm" onClick={openCreate}>
          Novo formulário
        </Button>
      </div>

      <ColetaResumo stats={coletaStats} loading={loadingStats} />

      <Card className="mt-2">
        <CardBody style={{ padding: 0 }}>
          <div className="d-flex" style={{ minHeight: 420 }}>
            {/* Sidebar */}
            <div
              style={{
                width: 280,
                flexShrink: 0,
                borderRight: '1px solid #e9ecef',
                background: '#f8f9fa',
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ marginBottom: 10, padding: '0 4px' }}
              >
                <span style={{ fontSize: 12, fontWeight: 700, color: '#6c757d', letterSpacing: 0.3 }}>
                  FORMULÁRIOS
                </span>
                <span style={{ fontSize: 12, color: '#6c757d' }}>{forms.length}</span>
              </div>

              <div style={{ flex: 1, overflowY: 'auto' }}>
                {forms.length === 0 ? (
                  <div
                    className="text-muted"
                    style={{
                      fontSize: 13,
                      padding: 12,
                      border: '1px dashed #dee2e6',
                      borderRadius: 6,
                      background: '#fff',
                    }}
                  >
                    Nenhum formulário neste projeto. Clique em <strong>Novo formulário</strong> para começar.
                  </div>
                ) : (
                  forms.map((f) => {
                    const active = Number(f.projForm_id) === Number(selectedFormId)
                    return (
                      <div
                        key={f.projForm_id}
                        role="button"
                        tabIndex={0}
                        style={sidebarItemStyle(active)}
                        onClick={() => setSelectedFormId(f.projForm_id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setSelectedFormId(f.projForm_id)
                          }
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: '#343a40',
                            lineHeight: 1.3,
                            wordBreak: 'break-word',
                          }}
                        >
                          {f.projForm_nome || `Formulário #${f.projForm_id}`}
                        </div>
                        <div className="d-flex justify-content-between" style={{ marginTop: 4, fontSize: 11, color: '#6c757d' }}>
                          <span>v{f.projForm_versao || '1'}</span>
                          <span>#{f.projForm_id}</span>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>

            {/* Main */}
            <div style={{ flex: 1, minWidth: 0, padding: 16 }}>
              {!selectedForm ? (
                <div className="d-flex flex-column align-items-center justify-content-center text-muted" style={{ minHeight: 280, fontSize: 13 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Selecione um formulário</div>
                  <div>As respostas coletadas aparecerão aqui.</div>
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 10, marginBottom: 14 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 16, color: '#343a40' }}>
                        {selectedForm.projForm_nome}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                        Versão {selectedForm.projForm_versao || '1'}
                        {' · '}
                        ID {selectedForm.projForm_id}
                        {selectedForm.projForm_dtCria
                          ? ` · criado em ${formatDateTime(selectedForm.projForm_dtCria)}`
                          : ''}
                      </div>
                    </div>
                    <div className="d-flex" style={{ gap: 8 }}>
                      <Button color="primary" outline size="sm" onClick={() => openEdit(selectedForm)}>
                        Editar formulário
                      </Button>
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        onClick={exportResponsesCsv}
                        disabled={loadingResps || exporting || resps.length === 0}
                        title={resps.length ? 'Baixar CSV para abrir no Excel' : 'Nenhuma resposta para exportar'}
                      >
                        {exporting ? 'Exportando…' : 'Exportar planilha'}
                      </Button>
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        onClick={() => {
                          loadResps(selectedFormId)
                          loadProjectResps(forms)
                        }}
                        disabled={loadingResps}
                      >
                        Atualizar
                      </Button>
                    </div>
                  </div>

                  {loadingResps ? (
                    <div className="d-flex align-items-center" style={{ gap: 10, padding: '20px 0' }}>
                      <Spinner size="sm" />
                      <span className="text-muted" style={{ fontSize: 13 }}>Carregando respostas…</span>
                    </div>
                  ) : resps.length === 0 ? (
                    <div
                      style={{
                        border: '1px solid #e9ecef',
                        borderRadius: 6,
                        background: '#f8f9fa',
                        padding: 20,
                        fontSize: 13,
                        color: '#6c757d',
                      }}
                    >
                      Nenhuma resposta coletada para este formulário ainda.
                    </div>
                  ) : (
                    <div style={{ border: '1px solid #e9ecef', borderRadius: 6, overflow: 'hidden' }}>
                      <Table responsive hover className="mb-0" style={{ fontSize: 13 }}>
                        <thead style={{ background: '#f8f9fa' }}>
                          <tr>
                            <th style={{ width: 70 }}>ID</th>
                            <th>Preenchido por</th>
                            <th style={{ width: 100 }}>Versão</th>
                            <th style={{ width: 150 }}>Data</th>
                            <th style={{ width: 90 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {resps.map((r) => (
                            <tr key={r.projFormResp_id}>
                              <td>{r.projFormResp_id}</td>
                              <td>{r.pf_nome || (r.pf_id ? `PF #${r.pf_id}` : '—')}</td>
                              <td>{r.projFormResp_versaoForm || '—'}</td>
                              <td>{formatDateTime(r.projFormResp_dtCria)}</td>
                              <td className="text-right">
                                <Button color="primary" outline size="sm" onClick={() => openResp(r)}>
                                  Ver
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Modal criar / editar form */}
      <Modal isOpen={editorOpen} toggle={closeEditor} size="lg" centered>
        <ModalHeader toggle={closeEditor}>
          {isEditing ? `Editar formulário #${editingFormId}` : 'Novo formulário'}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Nome</Label>
            <Input
              value={editNome}
              onChange={(e) => setEditNome(e.target.value)}
              placeholder="Ex.: FOR 15 - Ficha de Coleta"
              disabled={editorSaving}
            />
          </FormGroup>
          <FormGroup>
            <Label>Versão</Label>
            <Input
              value={editVersao}
              onChange={(e) => setEditVersao(e.target.value)}
              placeholder="1"
              disabled={editorSaving}
              style={{ maxWidth: 120 }}
            />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label>Definição (JSON — seções)</Label>
            <Input
              type="textarea"
              rows={12}
              value={editDef}
              onChange={(e) => setEditDef(e.target.value)}
              disabled={editorSaving}
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 12 }}
            />
            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
              Deve ser um array JSON no formato do FormTemplate (sections).
            </div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline disabled={editorSaving} onClick={closeEditor}>
            Cancelar
          </Button>
          <Button color="primary" disabled={editorSaving} onClick={requestSaveForm}>
            {editorSaving ? 'Salvando…' : isEditing ? 'Salvar alterações' : 'Criar formulário'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirmação: atualizar registro atual vs criar nova versão */}
      <Modal
        isOpen={saveChoiceOpen}
        toggle={() => !editorSaving && setSaveChoiceOpen(false)}
        centered
      >
        <ModalHeader toggle={() => !editorSaving && setSaveChoiceOpen(false)}>
          Como deseja salvar?
        </ModalHeader>
        <ModalBody>
          <p style={{ fontSize: 14, marginBottom: 12 }}>
            Você está editando <strong>{editingOriginal?.projForm_nome || 'este formulário'}</strong>
            {editingOriginal?.projForm_versao ? (
              <>
                {' '}
                (versão atual <strong>{editingOriginal.projForm_versao}</strong>)
              </>
            ) : null}
            .
          </p>
          <div
            style={{
              border: '1px solid #e9ecef',
              borderRadius: 6,
              padding: 12,
              marginBottom: 10,
              background: '#f8f9fa',
              fontSize: 13,
            }}
          >
            <strong>Atualizar a versão atual</strong>
            <div className="text-muted mt-1">
              Altera o registro #{editingFormId}. Respostas já coletadas continuam ligadas a este formulário;
              a definição muda para quem abrir daqui pra frente.
            </div>
          </div>
          <div
            style={{
              border: '1px solid #e9ecef',
              borderRadius: 6,
              padding: 12,
              background: '#f8f9fa',
              fontSize: 13,
            }}
          >
            <strong>Criar uma nova versão</strong>
            <div className="text-muted mt-1">
              Gera um novo formulário com a versão informada no campo
              {editVersao ? (
                <>
                  {' '}
                  (<strong>{editVersao}</strong>)
                </>
              ) : null}
              . O registro anterior permanece intacto para histórico e respostas antigas.
              {editingOriginal && String(editVersao || '') === String(editingOriginal.projForm_versao || '1') && (
                <div className="text-danger mt-2">
                  Altere o campo Versão antes de criar uma nova (não pode ser igual à atual).
                </div>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex flex-wrap" style={{ gap: 8 }}>
          <Button color="secondary" outline disabled={editorSaving} onClick={() => setSaveChoiceOpen(false)}>
            Voltar
          </Button>
          <Button
            color="secondary"
            disabled={editorSaving}
            onClick={() => handleSaveForm('update')}
          >
            {editorSaving ? 'Salvando…' : 'Atualizar versão atual'}
          </Button>
          <Button
            color="primary"
            disabled={
              editorSaving ||
              (editingOriginal &&
                String(editVersao || '') === String(editingOriginal.projForm_versao || '1'))
            }
            onClick={() => handleSaveForm('new_version')}
          >
            {editorSaving ? 'Salvando…' : 'Criar nova versão'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal ver resposta */}
      <Modal isOpen={respModalOpen} toggle={() => setRespModalOpen(false)} size="xl" centered>
        <ModalHeader toggle={() => setRespModalOpen(false)}>
          Resposta #{selectedResp?.projFormResp_id}
        </ModalHeader>
        <ModalBody>
          {selectedResp && (
            <>
              <div className="d-flex flex-wrap" style={{ gap: 16, marginBottom: 12, fontSize: 13 }}>
                <div>
                  <span className="text-muted">Por:</span>{' '}
                  <strong>{selectedResp.pf_nome || (selectedResp.pf_id ? `PF #${selectedResp.pf_id}` : '—')}</strong>
                </div>
                <div>
                  <span className="text-muted">Versão form:</span>{' '}
                  <strong>{selectedResp.projFormResp_versaoForm || '—'}</strong>
                </div>
                <div>
                  <span className="text-muted">Data:</span>{' '}
                  <strong>{formatDateTime(selectedResp.projFormResp_dtCria)}</strong>
                </div>
              </div>
              <div className="d-flex" style={{ gap: 8, marginBottom: 12 }}>
                <Button
                  size="sm"
                  color="primary"
                  outline={respView !== 'ficha'}
                  aria-pressed={respView === 'ficha'}
                  onClick={() => setRespView('ficha')}
                  disabled={respLoading}
                >
                  Ficha
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  outline={respView !== 'json'}
                  aria-pressed={respView === 'json'}
                  onClick={() => setRespView('json')}
                  disabled={respLoading}
                >
                  JSON
                </Button>
              </div>
              {respLoading ? (
                <div className="d-flex align-items-center" style={{ gap: 10, padding: '12px 0' }}>
                  <Spinner size="sm" />
                  <span className="text-muted" style={{ fontSize: 13 }}>Carregando coleta…</span>
                </div>
              ) : respView === 'json' ? (
                <pre
                  style={{
                    margin: 0,
                    padding: 12,
                    borderRadius: 6,
                    border: '1px solid #e9ecef',
                    background: '#f8f9fa',
                    fontSize: 12,
                    maxHeight: 420,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {tryPrettyJson(selectedResp.projFormResp_dados)}
                </pre>
              ) : (
                <FichaView groups={ficha.groups} defMissing={ficha.defMissing} />
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setRespModalOpen(false)}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default ProjDadosTab
